import React, {useMemo, useState} from "react"
import {Box, Button, IconButton, Typography} from "@mui/material"
import {styled} from "@mui/material/styles"
import {CirclePlus, Trash} from "lucide-react"
import Content from "../../components/Content"
import {cloneAndReplace, getMerkmal} from "../../misc/widget-functions"
import {buildDefaultEntry} from "../../misc/default-registry"
import IconButtonOutlined, {IconButtonComponent} from "../widgets/IconButtonOutlined"

export const containerObjectListPrototype = [
    {
        key: "text",
        type: "STRING",
        label: "Titel",
        size: 6,
    },
]

const InlineIconButton = styled(IconButton)(({theme}) => theme.FRAGEKATALOG.QuestionMulti.BORDERLESS.IconButton)
const IndexBadge = styled(Button, {
    shouldForwardProp: (prop) => prop !== "active",
})(({theme, active}) => theme.FRAGEKATALOG.QuestionMulti.BORDERLESS.IndexBadge(active))

const ContainerObjectList = ({container, value, onValueChange, disabled, contextArea, triggerAction}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const merkmal = getMerkmal(container)
    const entries = Array.isArray(value) ? value : []
    const children = Array.isArray(container?.children) ? container.children : []
    const hasPointer = Boolean(container?.connections?.origin?.pointer)
    const canEdit = hasPointer && typeof onValueChange === "function"

    const handleAdd = () => {
        if (!canEdit) return
        const newList = [...entries, buildDefaultEntry(merkmal?.pointer)]
        onValueChange(newList)
        setCurrentIndex(newList.length - 1)
        if (contextArea) {
            triggerAction("UPDATE_CONTEXTAREA", {contextArea, value: newList, merkmal})
        }
    }

    const handleDelete = () => {
        if (!canEdit || entries.length === 0) return
        const newList = entries.filter((_, index) => index !== currentIndex)
        onValueChange(newList)
        setCurrentIndex(Math.max(0, currentIndex - 1))
        if (contextArea) {
            triggerAction("UPDATE_CONTEXTAREA", {contextArea, value: newList, merkmal})
        }
    }

    const widgetList = useMemo(
        () => children.map((child) => cloneAndReplace(child, currentIndex)),
        [children, currentIndex],
    )

    return (
        <Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                {container?.text && (
                    <Typography variant="h5" sx={{fontWeight: 700}}>
                        {container.text}
                    </Typography>
                )}
                <Box sx={{display: "flex", gap: 1}}>
                    <IconButtonComponent
                        onClick={handleAdd}
                        color="primary"
                        disabled={!canEdit}>
                        <CirclePlus size={24} />
                    </IconButtonComponent>
                    {entries.length > 0 && (
                        <InlineIconButton
                            onClick={handleDelete}
                            color="primary"
                            disabled={!canEdit}>
                            <Trash size={20} />
                        </InlineIconButton>
                    )}
                </Box>
            </Box>
            {entries.length > 0 && widgetList.length > 0 && (
                <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 2}}>
                    {widgetList.map((widget, index) => (
                        <Content
                            key={`${currentIndex}-${index}`}
                            child={widget}
                            disabled={disabled}
                        />
                    ))}
                </Box>
            )}
            {entries.length > 1 && (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mt: 2}}>
                    {entries.map((_, index) => (
                        <IndexBadge
                            key={index}
                            active={index === currentIndex}
                            onClick={() => setCurrentIndex(index)}>
                            {index + 1}
                        </IndexBadge>
                    ))}
                </Box>
            )}
        </Box>
    )
}

export default ContainerObjectList
